import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_LANGUAGE, DEFAULT_THEME } from '../config/app';
import {
  loadLocalePreferences,
  resolvePrefsUserId,
  writeUserPreferences,
} from '../services/userPreferences';
import type { Language, ThemeName } from '../types';

export function usePersistedLocale(userId: string | null | undefined) {
  const [lang, setLangState] = useState<Language>(DEFAULT_LANGUAGE);
  const [theme, setThemeState] = useState<ThemeName>(DEFAULT_THEME);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const next = loadLocalePreferences(userId);
    setLangState(next.lang);
    setThemeState(next.theme);
    setHydrated(true);
  }, [userId]);

  useEffect(() => {
    if (!hydrated) return;
    writeUserPreferences(resolvePrefsUserId(userId), { lang, theme });
  }, [hydrated, userId, lang, theme]);

  const setLang = useCallback((value: Language | ((prev: Language) => Language)) => {
    setLangState((prev) => (typeof value === 'function' ? value(prev) : value));
  }, []);

  const setTheme = useCallback((value: ThemeName | ((prev: ThemeName) => ThemeName)) => {
    setThemeState((prev) => (typeof value === 'function' ? value(prev) : value));
  }, []);

  return { lang, setLang, theme, setTheme, hydrated };
}
